import React, { useMemo, useState } from "react";
import { StatusPill, EmptyState } from "../../../../components/ui/PageBits";
import { getPlan, effectiveLimit, effectiveTypeLimit, excessShopTypes, shopTypes, fmtLimit } from "../../../Booking/bookingPlans";
import { useShopStats, statusTone } from "./ownerBits";

function maskKey(key) {
    if (!key) return "—";
    return key.length > 14 ? `${key.slice(0, 14)}…` : key;
}

/** Shop directory: search, filter, onboard, suspend, delete, configure, impersonate. */
export default function ShopsTab({
    shops, resourceTypes, resources, bookings, plans,
    onAdd, onConfigure, onToggleStatus, onDelete, onImpersonate,
}) {
    const [q, setQ] = useState("");
    const [status, setStatus] = useState("all");
    const [plan, setPlan] = useState("all");
    const perShop = useShopStats(shops, resources, bookings, plans);

    const filtered = useMemo(() => {
        const needle = q.trim().toLowerCase();
        return shops.filter((s) => {
            if (status !== "all" && s.status !== status) return false;
            if (plan !== "all" && s.plan !== plan) return false;
            if (!needle) return true;
            return [s.business, s.owner, s.email].join(" ").toLowerCase().includes(needle);
        });
    }, [shops, q, status, plan]);

    const copyKey = async (key) => {
        try {
            await navigator.clipboard.writeText(key);
        } catch {
            const ta = document.createElement("textarea");
            ta.value = key;
            document.body.appendChild(ta);
            ta.select();
            try { document.execCommand("copy"); } catch { /* ignore */ }
            ta.remove();
        }
    };

    return (
        <div className="space-y-4">
            <div className="hk-card">
                <div className="flex flex-col md:flex-row gap-2">
                    <input
                        className="hk-input md:flex-1"
                        placeholder="Search shops, owners, emails…"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                    />
                    <div className="flex gap-2">
                        <select className="hk-select" value={status} onChange={(e) => setStatus(e.target.value)}>
                            <option value="all">All statuses</option>
                            <option value="active">Active</option>
                            <option value="suspended">Suspended</option>
                        </select>
                        <select className="hk-select" value={plan} onChange={(e) => setPlan(e.target.value)}>
                            <option value="all">All plans</option>
                            <option value="free">Free</option>
                            <option value="pro">Pro</option>
                        </select>
                        <button className="hk-btn hk-btn-primary shrink-0" onClick={onAdd}>
                            + Add shop
                        </button>
                    </div>
                </div>
            </div>

            <div className="hk-table-wrap">
                {filtered.length === 0 ? (
                    <EmptyState icon="🏪" title="No shops match" hint="Try a different search or onboard a new shop." />
                ) : (
                    <table className="hk-table">
                        <thead>
                            <tr>
                                <th>Shop</th>
                                <th>Plan · limits</th>
                                <th>Resource types</th>
                                <th>Bookings</th>
                                <th>API key</th>
                                <th>Status</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((s) => {
                                const st = perShop[s.id] || {};
                                const mine = shopTypes(resourceTypes, s.id);
                                const excess = excessShopTypes(s, resourceTypes, plans);
                                const limit = effectiveLimit(s, plans);
                                const tLimit = effectiveTypeLimit(s, plans);
                                return (
                                    <tr key={s.id}>
                                        <td>
                                            <div className="font-bold">{s.business}</div>
                                            <div className="text-xs text-[#64748b]">{s.owner} · {s.email}</div>
                                        </td>
                                        <td>
                                            <StatusPill tone={s.plan === "pro" ? "amber" : "zinc"}>
                                                {getPlan(plans, s.plan)?.label || s.plan}
                                            </StatusPill>
                                            <div className="text-xs text-[#64748b] mt-1 hk-mono">
                                                {st.resources}/{fmtLimit(limit)} res
                                            </div>
                                        </td>
                                        <td>
                                            <div className="text-xs max-w-[220px]">
                                                {mine.length ? mine.map((t) => t.name).join(", ") : "—"}
                                            </div>
                                            <div className="text-xs text-[#64748b] hk-mono mt-1">
                                                {mine.length}/{fmtLimit(tLimit)} types
                                                {excess.length > 0 && (
                                                    <span className="text-[#b45309] font-bold"> · {excess.length} over limit</span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="hk-mono text-xs whitespace-nowrap">
                                            {st.total} bookings
                                            {st.locked > 0 && <div className="text-[#b45309]">{st.locked} locked</div>}
                                        </td>
                                        <td>
                                            <button
                                                className="hk-mono text-xs text-[#64748b] hover:text-[#0f172a] underline"
                                                title="Copy full API key"
                                                onClick={() => copyKey(s.apiKey)}
                                            >
                                                {maskKey(s.apiKey)} ⧉
                                            </button>
                                        </td>
                                        <td><StatusPill tone={statusTone(s.status)}>{s.status}</StatusPill></td>
                                        <td className="text-right whitespace-nowrap">
                                            <div className="flex gap-1 justify-end flex-wrap">
                                                <button className="hk-btn hk-btn-primary hk-btn-sm" onClick={() => onImpersonate(s.id)} title="Open this shop's console as the shop owner">
                                                    🕶 Impersonate
                                                </button>
                                                <button className="hk-btn hk-btn-outline hk-btn-sm" onClick={() => onConfigure(s.id)}>
                                                    Configure
                                                </button>
                                                <button
                                                    className="hk-btn hk-btn-outline hk-btn-sm"
                                                    onClick={() => onToggleStatus(s)}
                                                    title={s.status === "active" ? "Suspend shop (blocks new bookings)" : "Reactivate shop"}
                                                >
                                                    {s.status === "active" ? "Suspend" : "Activate"}
                                                </button>
                                                <button
                                                    className="hk-btn hk-btn-outline hk-btn-sm"
                                                    style={{ color: "var(--hk-danger)" }}
                                                    onClick={() => onDelete(s)}
                                                >
                                                    Delete
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>
            <p className="text-xs text-[#64748b] px-1">
                Suspended shops reject new bookings (existing history is kept). Deleting a shop orphans its
                bookings into platform history instead of erasing them.
            </p>
        </div>
    );
}

